/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

import { useApi } from '@/hooks'

import { DEFAULT_COORD_DIGITS, DEFAULT_NUMBER_DIGITS, type MigrationDefaults, type MigrationFilters } from './types'

const num = (v: string | null) => (v && Number.isFinite(Number(v)) ? Number(v) : undefined)

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * The server sends wall-clock strings, but apiService turns anything date-like in a response
 * into a Date (parsed as local time). Put it back to `yyyy-MM-ddTHH:mm:ss` before it goes into
 * the URL — `String(date)` would give "Fri Oct 02 2026 …", which no endpoint can parse.
 */
const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/

export const wallClock = (v: string | Date | null | undefined): string | undefined => {
  if (v instanceof Date) {
    return Number.isNaN(v.getTime())
      ? undefined
      : `${String(v.getFullYear())}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}T${pad(v.getHours())}:${pad(
          v.getMinutes()
        )}:${pad(v.getSeconds())}`
  }
  if (!v) return undefined
  // Anything else in the URL (e.g. a link saved before this was fixed): reparse it, or drop it
  // so the defaults fill it back in.
  return WALL_CLOCK.test(v) ? v : wallClock(new Date(v))
}

/**
 * The dashboard's company / asset / date range, kept in the URL so the item and compare pages
 * open with the same filters. When the URL has no company, the server's defaults fill it in:
 * ALB SoCal, yesterday, and an asset with data in that window.
 */
export const useMigrationFilters = () => {
  const { api } = useApi()
  const [params, setParams] = useSearchParams()
  const [defaultAssetName, setDefaultAssetName] = useState<string>()
  const [loadingDefaults, setLoadingDefaults] = useState(false)
  // Defaults are fetched once, on arrival: after that the user owns the filters.
  const defaultsRequested = useRef(false)

  const filters = useMemo<MigrationFilters>(
    () => ({
      companyId: num(params.get('companyId')),
      assetId: num(params.get('assetId')),
      from: wallClock(params.get('from')),
      to: wallClock(params.get('to')),
      coordDigits: num(params.get('coordDigits')) ?? DEFAULT_COORD_DIGITS,
      numberDigits: num(params.get('numberDigits')) ?? DEFAULT_NUMBER_DIGITS,
    }),
    [params]
  )

  const setFilters = useCallback(
    (patch: Partial<MigrationFilters>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === '') next.delete(k)
            else next.set(k, String(v))
          }
          return next
        },
        { replace: true }
      )
    },
    [setParams]
  )

  // Fill in whatever the URL lacks from the server's defaults, once.
  useEffect(() => {
    if (defaultsRequested.current) return
    defaultsRequested.current = true
    if (filters.companyId && filters.from && filters.to) return
    // No cancellation: the ref makes this run once, so a StrictMode re-run must not drop it.
    const load = async () => {
      setLoadingDefaults(true)
      const query = new URLSearchParams()
      if (filters.companyId) query.set('companyId', String(filters.companyId))
      const res = await api.get<MigrationDefaults>({
        path: `/app/admin/migration/defaults?${query.toString()}`,
        origin: 'MigrationDashboard#defaults',
      })
      setLoadingDefaults(false)
      if (!res.data) return
      setDefaultAssetName(res.data.asset?.name)
      setFilters({
        companyId: filters.companyId ?? res.data.companyId,
        assetId: filters.assetId ?? res.data.asset?.assetId,
        from: filters.from ?? wallClock(res.data.from),
        to: filters.to ?? wallClock(res.data.to),
      })
    }
    void load()
  }, [api, filters, setFilters])

  /** The query string to carry the filters onto another dashboard page. */
  const search = params.toString()

  return { filters, setFilters, search, defaultAssetName, loadingDefaults }
}
